import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  History, Settings, Mail, Users, Send, CheckCircle, XCircle,
  AlertTriangle, Loader2, Clock, Activity, TrendingUp, ChevronDown,
  ChevronUp, RefreshCw, Layers
} from 'lucide-react';

// Traducción de códigos técnicos de "tipo" a texto legible para la columna Detalle.
const TIPO_DETALLE = {
  barrida_inicial: 'Barrida inicial del padrón',
  deteccion_cuentas_nuevas: 'Detección de cuentas nuevas',
  auto_corrida_movimientos: 'Alta automática (post-corrida de movimientos)',
  enviar_email: 'Envío de emails',
  reenviar_email: 'Reenvío de emails',
  buscar_email_waya: 'Búsqueda de emails en Waya',
  verificar_cierre: 'Verificación de cierre en Waya',
  cierre_definitivo: 'Cierre definitivo de cuentas',
  revalidacion: 'Revalidación de alertas',
  generar_casos: 'Generación de casos',
  cvu: 'Búsqueda por CVU',
  cuit: 'Búsqueda por CUIT',
};


export const ExecutionHistory = () => {
  const [activeTab, setActiveTab] = useState('history');
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [groups, setGroups] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [expandedExecution, setExpandedExecution] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [bgJobs, setBgJobs] = useState([]);
  const [bgLoading, setBgLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadBgJobs = async () => {
    setBgLoading(true);
    try {
      const r = await api.get('/background-jobs?limit=150');
      setBgJobs(r.data?.items || []);
    } catch { /* noop */ } finally { setBgLoading(false); }
  };

  const limpiarColgados = async () => {
    if (!window.confirm('¿Marcar como cancelados los trabajos que quedaron colgados (procesando hace más de 20 min)? Sirve para limpiar jobs interrumpidos por un reinicio/deploy.')) return;
    try {
      const r = await api.post('/background-jobs/limpiar-colgados');
      toast.success(`${r.data?.cancelados || 0} trabajo(s) colgado(s) marcados como cancelados`);
      loadBgJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo limpiar'); }
  };

  useEffect(() => {
    if (activeTab !== 'background') return;
    loadBgJobs();
    const iv = setInterval(loadBgJobs, 5000);
    return () => clearInterval(iv);
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [executionsRes, statsRes, configRes, groupsRes, userGroupsRes] = await Promise.all([
        api.get('/monitor-notifications/executions?limit=50'),
        api.get('/monitor-notifications/executions/stats?days=30'),
        api.get('/monitor-notifications/config'),
        api.get('/monitor-groups'),
        api.get('/groups')
      ]);
      
      setExecutions(executionsRes.data);
      setStats(statsRes.data);
      setConfig(configRes.data);
      setGroups(groupsRes.data);
      setUserGroups(userGroupsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async () => {
    setSaving(true);
    try {
      const newValue = !config.enabled;
      await api.patch('/monitor-notifications/config', { enabled: newValue });
      setConfig({ ...config, enabled: newValue });
      toast.success(newValue ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
    } catch (error) {
      toast.error('Error al actualizar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleIncludeDetail = async () => {
    setSaving(true);
    try {
      const newValue = !config.include_detail;
      await api.patch('/monitor-notifications/config', { include_detail: newValue });
      setConfig({ ...config, include_detail: newValue });
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSendOnZero = async () => {
    setSaving(true);
    try {
      const newValue = !config.send_on_zero_alerts;
      await api.patch('/monitor-notifications/config', { send_on_zero_alerts: newValue });
      setConfig({ ...config, send_on_zero_alerts: newValue });
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecipient = async () => {
    if (!newRecipient || !newRecipient.includes('@')) {
      toast.error('Ingrese un email válido');
      return;
    }

    const currentRecipients = config.recipients || [];
    if (currentRecipients.includes(newRecipient)) {
      toast.error('Este email ya está en la lista');
      return;
    }

    setSaving(true);
    try {
      const updatedRecipients = [...currentRecipients, newRecipient];
      await api.patch('/monitor-notifications/config', { recipients: updatedRecipients });
      setConfig({ ...config, recipients: updatedRecipients });
      setNewRecipient('');
      toast.success('Destinatario agregado');
    } catch (error) {
      toast.error('Error al agregar destinatario');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRecipient = async (email) => {
    setSaving(true);
    try {
      const updatedRecipients = (config.recipients || []).filter(r => r !== email);
      await api.patch('/monitor-notifications/config', { recipients: updatedRecipients });
      setConfig({ ...config, recipients: updatedRecipients });
      toast.success('Destinatario eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleGroup = async (groupId) => {
    const currentGroups = config.recipient_groups || [];
    let updatedGroups;
    
    if (currentGroups.includes(groupId)) {
      updatedGroups = currentGroups.filter(g => g !== groupId);
    } else {
      updatedGroups = [...currentGroups, groupId];
    }

    setSaving(true);
    try {
      await api.patch('/monitor-notifications/config', { recipient_groups: updatedGroups });
      setConfig({ ...config, recipient_groups: updatedGroups });
      toast.success('Grupos actualizados');
    } catch (error) {
      toast.error('Error al actualizar grupos');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const result = await api.post('/monitor-notifications/test');
      if (result.data.success) {
        toast.success(result.data.message);
      } else {
        toast.error(result.data.message);
      }
    } catch (error) {
      toast.error('Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffMs = endDate - startDate;
      const seconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      
      if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
      }
      return `${seconds}s`;
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Historial de Ejecuciones
          </h1>
          <p className="text-slate-600 mt-1">
            Resultados de ejecuciones de monitores y configuración de notificaciones
          </p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="mt-4 lg:mt-0"
        >
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_ejecuciones || 0}</p>
              <p className="text-sm text-slate-500">Ejecuciones (30 días)</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_coincidencias || 0}</p>
              <p className="text-sm text-slate-500">Coincidencias Totales</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="text-red-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_alertas || 0}</p>
              <p className="text-sm text-slate-500">Alertas Generadas</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.monitores_ejecutados || 0}</p>
              <p className="text-sm text-slate-500">Monitores Ejecutados</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <History size={16} className="mr-2" />
            Historial
          </TabsTrigger>
          <TabsTrigger value="background" className="data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700" data-testid="tab-background-jobs">
            <Layers size={16} className="mr-2" />
            Trabajos en 2º plano
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-green-50 data-[state=active]:text-green-700">
            <Mail size={16} className="mr-2" />
            Notificaciones
          </TabsTrigger>
        </TabsList>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Fecha/Hora</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead className="text-center">Monitores</TableHead>
                  <TableHead className="text-center">Coincidencias</TableHead>
                  <TableHead className="text-center">Alertas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executions.map((exec) => (
                  <React.Fragment key={exec.id}>
                    <TableRow 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedExecution(expandedExecution === exec.id ? null : exec.id)}
                    >
                      <TableCell>
                        {expandedExecution === exec.id ? (
                          <ChevronUp size={16} className="text-slate-400" />
                        ) : (
                          <ChevronDown size={16} className="text-slate-400" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{exec.group_nombre}</TableCell>
                      <TableCell>{formatDate(exec.fecha_inicio)}</TableCell>
                      <TableCell>
                        <span className="text-slate-500">
                          <Clock size={14} className="inline mr-1" />
                          {formatDuration(exec.fecha_inicio, exec.fecha_fin)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{exec.monitores_ejecutados}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{exec.total_coincidencias}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-bold ${exec.total_alertas_generadas > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {exec.total_alertas_generadas}
                        </span>
                      </TableCell>
                    </TableRow>
                    
                    {expandedExecution === exec.id && exec.resultados_por_monitor && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-slate-50 p-0">
                          <div className="p-4">
                            <h4 className="text-sm font-medium text-slate-700 mb-3">Detalle por Monitor</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {exec.resultados_por_monitor.map((r, idx) => (
                                <div 
                                  key={idx} 
                                  className={`p-3 rounded-lg border ${
                                    r.estado === 'EJECUTADO' ? 'bg-white border-slate-200' :
                                    r.estado === 'OMITIDO' ? 'bg-yellow-50 border-yellow-200' :
                                    'bg-red-50 border-red-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium truncate">{r.monitor_nombre}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      r.estado === 'EJECUTADO' ? 'bg-green-100 text-green-700' :
                                      r.estado === 'OMITIDO' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {r.estado}
                                    </span>
                                  </div>
                                  <div className="flex gap-4 text-xs text-slate-500">
                                    <span>Coinc: <strong className="text-slate-700">{r.coincidencias || 0}</strong></span>
                                    <span>Alertas: <strong className={r.alertas_generadas > 0 ? 'text-red-600' : 'text-slate-700'}>{r.alertas_generadas || 0}</strong></span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {exec.errores && exec.errores.length > 0 && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm font-medium text-red-700 mb-1">Errores:</p>
                                <ul className="text-xs text-red-600 space-y-1">
                                  {exec.errores.map((err, idx) => (
                                    <li key={idx}>• {err}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                
                {executions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <History size={40} className="mx-auto mb-3 opacity-50" />
                      No hay ejecuciones registradas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* BACKGROUND JOBS TAB */}
        <TabsContent value="background" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-bold text-slate-900">Trabajos en segundo plano</h3>
                <p className="text-sm text-slate-500">Verificaciones Waya, importaciones, generación de casos/ROS, extracciones, altas masivas y más. Se actualiza solo cada 5s.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={limpiarColgados} variant="outline" size="sm" data-testid="bg-jobs-limpiar-colgados" className="text-amber-700 border-amber-200 hover:bg-amber-50">
                  Limpiar colgados
                </Button>
                <Button onClick={loadBgJobs} variant="outline" size="sm" data-testid="bg-jobs-refresh">
                  <RefreshCw size={14} className={`mr-2 ${bgLoading ? 'animate-spin' : ''}`} /> Actualizar
                </Button>
              </div>
            </div>
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-[15%]">Tipo</TableHead>
                  <TableHead className="w-[31%]">Detalle</TableHead>
                  <TableHead className="w-[16%]">Usuario</TableHead>
                  <TableHead className="w-[12%] text-center">Estado</TableHead>
                  <TableHead className="w-[14%]">Progreso</TableHead>
                  <TableHead className="w-[12%]">Actualizado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bgJobs.map((j, idx) => (
                  <TableRow key={j.id || idx} data-testid={`bg-job-row-${idx}`}>
                    <TableCell className="font-medium text-sm truncate" title={j.tipo_label}>{j.tipo_label}</TableCell>
                    <TableCell className="text-sm text-slate-500 truncate" title={j.descripcion || TIPO_DETALLE[j.tipo] || j.tipo || ''}>{j.descripcion || TIPO_DETALLE[j.tipo] || j.tipo || '—'}</TableCell>
                    <TableCell className="text-sm truncate" title={j.usuario || ''}>{j.usuario || '—'}</TableCell>
                    <TableCell className="text-center">
                      {j.estado_norm === 'procesando' ? (
                        <Badge className="bg-blue-100 text-blue-700"><Loader2 size={11} className="mr-1 animate-spin inline" />En curso</Badge>
                      ) : j.estado_norm === 'completado' ? (
                        <Badge className="bg-green-100 text-green-700">Completado</Badge>
                      ) : j.estado_norm === 'error' ? (
                        <Badge className="bg-red-100 text-red-700">Error</Badge>
                      ) : (
                        <Badge variant="outline">{j.estado || '—'}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(j.total || j.procesados != null) ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                            <div className={`h-full ${j.estado_norm === 'error' ? 'bg-red-400' : j.estado_norm === 'completado' ? 'bg-green-500' : 'bg-blue-500'}`}
                              style={{ width: `${j.total ? Math.min(100, Math.round((j.procesados || 0) / j.total * 100)) : (j.estado_norm === 'completado' ? 100 : 5)}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{j.procesados ?? 0}{j.total ? `/${j.total}` : ''}</span>
                        </div>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 truncate">{formatDate(j.updated_at)}</TableCell>
                  </TableRow>
                ))}
                {bgJobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Layers size={40} className="mx-auto mb-3 opacity-50" />
                      {bgLoading ? 'Cargando…' : 'No hay trabajos en segundo plano registrados'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* NOTIFICATIONS TAB */}
        <TabsContent value="notifications" className="space-y-6">
          {/* Main Toggle */}
          <Card className="p-6 bg-white border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Notificaciones por Email</h3>
                <p className="text-sm text-slate-500">
                  Enviar resumen automático al finalizar la ejecución de un grupo de monitores
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${config?.enabled ? 'text-green-600' : 'text-slate-400'}`}>
                  {config?.enabled ? 'Activado' : 'Desactivado'}
                </span>
                <Switch
                  checked={config?.enabled || false}
                  onCheckedChange={handleToggleEnabled}
                  disabled={saving}
                />
              </div>
            </div>
          </Card>

          {/* Recipients Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Individual Recipients */}
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="text-blue-600" size={20} />
                <h3 className="font-bold text-slate-900">Destinatarios Individuales</h3>
              </div>
              
              <div className="flex gap-2 mb-4">
                <Input
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                />
                <Button onClick={handleAddRecipient} disabled={saving}>
                  Agregar
                </Button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(config?.recipients || []).map((email) => (
                  <div 
                    key={email} 
                    className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                  >
                    <span className="text-sm">{email}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                      onClick={() => handleRemoveRecipient(email)}
                    >
                      <XCircle size={16} />
                    </Button>
                  </div>
                ))}
                {(config?.recipients || []).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No hay destinatarios individuales
                  </p>
                )}
              </div>
            </Card>

            {/* Group Recipients */}
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Users className="text-purple-600" size={20} />
                <h3 className="font-bold text-slate-900">Grupos de Usuarios</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Todos los usuarios activos de estos grupos recibirán el email
              </p>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {userGroups.map((group) => (
                  <div 
                    key={group.id} 
                    className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg"
                  >
                    <Checkbox
                      checked={(config?.recipient_groups || []).includes(group.id)}
                      onCheckedChange={() => handleToggleGroup(group.id)}
                    />
                    <div>
                      <p className="text-sm font-medium">{group.nombre}</p>
                      <p className="text-xs text-slate-500">{group.descripcion || 'Sin descripción'}</p>
                    </div>
                  </div>
                ))}
                {userGroups.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No hay grupos de usuarios
                  </p>
                )}
              </div>
            </Card>
          </div>

          {/* Options */}
          <Card className="p-6 bg-white border-slate-200">
            <h3 className="font-bold text-slate-900 mb-4">Opciones del Email</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-700">Incluir detalle por monitor</p>
                  <p className="text-sm text-slate-500">Mostrar el resultado de cada monitor en el email</p>
                </div>
                <Switch
                  checked={config?.include_detail || false}
                  onCheckedChange={handleToggleIncludeDetail}
                  disabled={saving}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-700">Enviar aunque no haya alertas</p>
                  <p className="text-sm text-slate-500">Enviar email incluso cuando no se generen alertas</p>
                </div>
                <Switch
                  checked={config?.send_on_zero_alerts || false}
                  onCheckedChange={handleToggleSendOnZero}
                  disabled={saving}
                />
              </div>
            </div>
          </Card>

          {/* Test Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleTestEmail}
              disabled={testing || (config?.recipients || []).length === 0 && (config?.recipient_groups || []).length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {testing ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Enviar Email de Prueba
                </>
              )}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExecutionHistory;
